// Template authoring, deployment and scheduling.
//
// Authoring is executive-only server-side; deploying is developer. The UI
// mirrors that by disabling rather than hiding, so a developer can see what
// exists and why they cannot edit it.

const BASE = '/team-tools/api'

export type LootPool = {
    currency: string
    amount: string
    reward_min: string
    reward_max: string
    auto_add_from_spawn_fee: string
}

/** A drawer on the deploy screen. Organising only; deploys nothing. */
export type TemplateGroup = {
    id: number
    name: string
    collapsed: boolean
}

export type Template = {
    id: number
    name: string
    enabled: boolean
    /** Drawer this template is filed under, or null for the loose list. */
    group_id: number | null
    /** Hand-dragged position. The list arrives already sorted by it. */
    sort_order: number
    /** Slot ranges this template may deploy into; one is chosen at deploy time. */
    deploy_slot_regions: string[]
    /** Derived from the above by the backend — us and sa both report as US. */
    regions: string[]
    server_name_template: string
    example_name: string
    fee: string | null
    max_players: number | null
    map_id: number
    map_name: string
    password: string | null
    activate_on_apply: boolean
    check_requirements: boolean
    min_sessions: number | null
    max_sessions: number | null
    min_kills: number | null
    max_kills: number | null
    loot_update_frequency: number
    loot_max_spawn_points: number
    loot_pools: LootPool[]
    tournaments: Array<{ id: number; name: string }>
    matching_servers: number
    /** Live servers built from this kind. What "running on N" counts. */
    running_servers: number
}

export type WindowTemplate = {
    profile_id: number
    profile?: string
    count: number
    /** Blank uses the template's region when it carries exactly one. */
    region?: string
    /** The template's available regions, so the picker needs no extra fetch. */
    regions?: string[]
}

export type Window = {
    id: number
    name: string
    enabled: boolean
    /** First template only, kept for anything still reading the old shape. */
    profile_id: number
    profile: string | null
    count: number
    /** What the schedule actually deploys. */
    templates: WindowTemplate[]
    summary: string
    total_servers: number
    /** What the servers will be called, one per server. */
    server_names?: string[]
    deploy_at: string
    destroy_at: string | null
    repeat_weekly: boolean
    last_deployed_at: string | null
    /** Up right now: its servers, and when THIS run tears down. Null when the
     *  schedule is not mid-run. */
    live?: { ends_at: string | null; servers: Array<{ id: number; name: string; slot: string | null }> } | null
}

export type Deployment = {
    /** PendingDeployment id; what dismiss is addressed by. */
    id: number
    slot: string
    template: string | null
    requested_by: string | null
    created_at: string
    age_seconds: number
    run_status: string | null
    run_conclusion: string | null
    run_url: string | null
}

export type SlotRegion = {
    id: string
    label: string
    /** What a server deployed here reports as its region. */
    server_region: string
}

/** The shared Server Creation rules (D-019): one row for everyone, where each
 *  browser used to keep its own copy. Keys are the page's own Rules keys. */
export type ServerRules = {
    noobMax: number
    proMin: number
    usdcPerFee: number
    usdcMinDiv: number
    usdcMaxDiv: number
    pointPerFee: number
    pointMinDiv: number
    pointMaxDiv: number
    updatedAt: string | null
    updatedBy: string | null
}

export type Options = {
    maps: Array<{ id: number; name: string }>
    currencies: string[]
    slot_regions: SlotRegion[]
    tournaments: Array<{ id: number; name: string }>
    rules?: ServerRules
    /** Read-only: how a paid spawn fee splits between AIRDROP and USDC. */
    fee_split?: { airdrop_percent: string; usdc_percent: string }
}

const csrf = () =>
    document.cookie
        .split('; ')
        .find((c) => c.startsWith('csrftoken='))
        ?.split('=')[1] ?? ''

async function send(path: string, method: string, body?: unknown) {
    const response = await fetch(`${BASE}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
        body: body === undefined ? undefined : JSON.stringify(body),
    })
    let data: any = {}
    try {
        data = await response.json()
    } catch {
        // 403 from the permission mixin has a body; a 500 may not.
    }
    if (!response.ok) {
        throw new Error(data.error || `${method} ${path} failed (${response.status})`)
    }
    return data
}

export const getTemplates = (): Promise<{
    templates: Template[]
    groups: TemplateGroup[]
    windows: Window[]
}> => send('/templates', 'GET')

export const getOptions = (): Promise<Options> => send('/template-options', 'GET')

export const getDeployments = (): Promise<{ deployments: Deployment[] }> =>
    send('/deployments', 'GET')

export type DeploymentLogRow = {
    id: number
    slot: string
    state: 'registered' | 'waiting' | 'stalled' | 'dismissed'
    template: string | null
    requested_by: string | null
    created_at: string
    age_seconds: number
    consumed_at: string | null
    dismissed_at: string | null
    dismissed_by: string | null
    server_id: number | null
    server_name: string | null
    dismissable: boolean
}

export const getDeploymentLog = (limit = 50): Promise<{ deployments: DeploymentLogRow[] }> =>
    send(`/deployments/log?limit=${limit}`, 'GET')

/** Closes a deployment that is never arriving, which releases its slot. */
export const dismissDeployment = (id: number) => send(`/deployments/${id}/dismiss`, 'POST')

/** What a loot pool's draw actually pays, for the label (D-021).
 *
 *  `LootRewardPool.spawn_loot` draws `randrange(min, max, 10)` in minor units
 *  (cents for USDC, whole points for POINT): steps of 0.10 USDC or 10 points
 *  up from the minimum, and never the maximum itself. The screens used to
 *  promise "min to max", and the max could never be drawn. The decision was to
 *  make the label honest rather than change the draw, so this mirrors the
 *  draw exactly. Null when the numbers cannot be drawn from. */
export const lootDrawRange = (
    currency: string,
    min: string | number | null | undefined,
    max: string | number | null | undefined
): { low: string; high: string; step: string | null } | null => {
    const unit = currency === 'POINT' ? 1 : 100
    const lo = Math.trunc(Number(min) * unit + 1e-9)
    const hi = Math.trunc(Number(max) * unit + 1e-9)
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi < lo) return null
    const show = (n: number) => (unit === 1 ? String(n) : (n / unit).toFixed(2))
    if (lo === hi) return { low: show(lo), high: show(lo), step: null }
    const top = lo + 10 * Math.floor((hi - lo - 1) / 10)
    return { low: show(lo), high: show(top), step: show(10) }
}

/** The honest one-line version of `lootDrawRange`. */
export const lootDrawLabel = (
    currency: string,
    min: string | number | null | undefined,
    max: string | number | null | undefined
): string => {
    const r = lootDrawRange(currency, min, max)
    if (!r) return ''
    const unit = currency === 'POINT' ? 'points' : 'USDC'
    if (!r.step) return `pays ${r.low} ${unit} a drop`
    return `pays ${r.low} to ${r.high} ${unit} a drop, in steps of ${r.step} (never the max typed)`
}

/** Executive only, like writing a template. Answers with the saved rules. */
export const saveServerRules = (rules: Partial<ServerRules>): Promise<{ rules: ServerRules }> =>
    send('/server-rules', 'POST', rules)

export const saveTemplate = (id: number | null, data: any) =>
    send(id ? `/templates/${id}` : '/templates/new', 'POST', data)

export const deleteTemplate = (id: number) => send(`/templates/${id}`, 'DELETE')

/**
 * Store the whole arrangement, not the move that produced it.
 *
 * The screen already holds the list it just rendered, so sending it back
 * entire is both simpler and the only version that behaves predictably when
 * two operators drag at once: last write wins, visibly, instead of the server
 * replaying two moves against an order neither of them was looking at.
 */
export const reorderTemplates = (
    templates: Array<{ id: number; group_id: number | null }>,
    groups?: Array<{ id: number }>
) =>
    send('/templates/reorder', 'POST', {
        templates,
        ...(groups ? { groups } : {}),
    })

export const saveGroup = (id: number | null, data: Partial<TemplateGroup>) =>
    send(id ? `/template-groups/${id}` : '/template-groups/new', 'POST', data)

/** Deletes the drawer only. Its templates come back as ungrouped. */
export const deleteGroup = (id: number) => send(`/template-groups/${id}`, 'DELETE')

// GCP machine families the create workflow accepts. c3 is preferred but scarce
// — a stockout is per machine-type-per-zone, and when c3 is short it tends to
// be short across a whole region. The workflow falls back to n2 on its own;
// naming one here skips the four failed applies first.
export const MACHINE_TYPES = [
    { value: 'default', label: 'Default (c3, falls back to n2)' },
    { value: 'c3-standard-4', label: 'c3 (fastest, scarcest)' },
    { value: 'n2-standard-4', label: 'n2 (slower, usually available)' },
    { value: 'n2d-standard-4', label: 'n2d (AMD, best availability)' },
]

/** A kill race a server being built now could join. */
export type JoinableRace = {
    id: number
    name: string
    begins_at: string
    ends_at: string
    running: boolean
    enabled: boolean
    servers: number
}

/** Live and scheduled kill races, for the make-a-server form. */
export const getJoinableRaces = (): Promise<{ races: JoinableRace[] }> =>
    send('/server/joinable-races', 'GET')

/** `region` picks which slot range; `machineType` which GCP family.
 *
 *  `joinTournaments` is remembered against each pending deployment and acted
 *  on when the server registers -- it cannot be applied now, because the
 *  server does not exist yet. */
export const deployTemplate = (
    id: number,
    count: number,
    region?: string,
    machineType?: string,
    joinTournaments?: number[]
) =>
    send(`/templates/${id}/deploy`, 'POST', {
        count,
        ...(region ? { region } : {}),
        ...(machineType && machineType !== 'default' ? { machine_type: machineType } : {}),
        ...(joinTournaments?.length ? { join_tournaments: joinTournaments } : {}),
    })

/** Retry a failed deployment on the same slot, optionally on another family. */
export const redeployDeployment = (id: number, machineType?: string) =>
    send(`/deployments/${id}/redeploy`, 'POST', {
        ...(machineType && machineType !== 'default' ? { machine_type: machineType } : {}),
    })

export const saveWindow = (id: number | null, data: any) =>
    send(id ? `/windows/${id}` : '/windows/new', 'POST', data)

export const deleteWindow = (id: number) => send(`/windows/${id}`, 'DELETE')
